import { api } from '@/lib/api/api'
import { CopilotSession } from 'sprites-types/api/v2/copilot'

export interface SessionPagination {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface SessionsResponse {
  sessions: CopilotSession[]
  pagination: SessionPagination
}

export interface GetSessionsParams {
  limit?: number
  offset?: number
  includeInteractions?: boolean
  bookmarked?: boolean
  businessProfileId?: string
}

export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllUserSessions: builder.query<SessionsResponse, GetSessionsParams>({
      query: (params: GetSessionsParams = {}) => {
        const {
          limit = 20,
          offset = 0,
          includeInteractions = false,
          bookmarked,
          businessProfileId,
        } = params
        const searchParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
          includeInteractions: includeInteractions.toString(),
        })

        if (bookmarked !== undefined) {
          searchParams.set('bookmarked', bookmarked.toString())
        }

        if (businessProfileId !== undefined) {
          searchParams.set('businessProfileId', businessProfileId)
        }

        return {
          url: `v2/copilot/getUserSessions?${searchParams}`,
          credentials: 'include',
        }
      },
      transformResponse: (response: {
        sessions: CopilotSession[]
        pagination: SessionPagination
      }): SessionsResponse => {
        return {
          sessions: response.sessions,
          pagination: response.pagination,
        }
      },
      providesTags: ['Sessions'],
    }),
    deleteSession: builder.mutation<
      { success: boolean; message: string; sessionId: string },
      string
    >({
      queryFn: async (sessionId: string) => {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/copilot/session/${sessionId}`,
          {
            method: 'DELETE',
            credentials: 'include',
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to delete session')
        }

        return await response.json()
      },
      invalidatesTags: ['Sessions'],
    }),
    getSession: builder.query<CopilotSession, string>({
      query: (sessionId) => ({
        url: `v2/copilot/session/${sessionId}`,
        credentials: 'include',
      }),
      transformResponse: (response: { session: CopilotSession }) => response.session,
      providesTags: (_result, _error, sessionId) => [{ type: 'Sessions', id: sessionId }],
    }),
    updateSession: builder.mutation<
      { success: boolean; message: string; session: CopilotSession },
      { sessionId: string; name: string }
    >({
      query: ({ sessionId, name }) => ({
        url: `v2/copilot/session/${sessionId}`,
        method: 'PUT',
        credentials: 'include',
        body: { name },
      }),
      async onQueryStarted({ sessionId, name }, { dispatch, queryFulfilled, getState }) {
        const patches: Array<{ undo: () => void }> = []

        // Update individual session cache
        try {
          const patch = dispatch(
            userApi.util.updateQueryData('getSession', sessionId, (draft) => {
              draft.threadSummary = name
            })
          )
          patches.push(patch)
        } catch {
          // Cache entry might not exist
        }

        // Update all session list caches
        const state = getState() as any
        const apiState = state.api
        if (apiState?.queries) {
          Object.keys(apiState.queries).forEach((key) => {
            if (key.startsWith('getAllUserSessions(')) {
              try {
                const match = key.match(/getAllUserSessions\((.*?)\)/)
                if (match) {
                  const params = match[1] ? JSON.parse(match[1]) : undefined
                  const patch = dispatch(
                    userApi.util.updateQueryData('getAllUserSessions', params, (draft) => {
                      const session = draft.sessions.find((s) => s.id === sessionId)
                      if (session) {
                        session.threadSummary = name
                      }
                    })
                  )
                  patches.push(patch)
                }
              } catch {
                // Skip if parsing fails
              }
            }
          })
        }

        try {
          await queryFulfilled
        } catch {
          patches.forEach((p) => p.undo())
        }
      },
      invalidatesTags: (_result, _error, { sessionId }) => [
        'Sessions',
        { type: 'Sessions', id: sessionId },
      ],
    }),
    setSessionBookmark: builder.mutation<
      { success: boolean; isBookmarked: boolean; message: string },
      { sessionId: string; bookmarked: boolean }
    >({
      query: ({ sessionId, bookmarked }) => ({
        url: `v2/copilot/sessions/${sessionId}/bookmark`,
        method: 'POST',
        credentials: 'include',
        body: { bookmarked },
      }),
      async onQueryStarted({ sessionId, bookmarked }, { dispatch, queryFulfilled, getState }) {
        const patches: Array<{ undo: () => void }> = []

        // Update individual session cache
        try {
          const patch = dispatch(
            userApi.util.updateQueryData('getSession', sessionId, (draft) => {
              draft.isBookmarked = bookmarked
            })
          )
          patches.push(patch)
        } catch {
          // Cache entry might not exist
        }

        // Update all session list caches (there might be multiple with different params)
        const state = getState() as any
        const apiState = state.api
        if (apiState?.queries) {
          Object.keys(apiState.queries).forEach((key) => {
            if (key.startsWith('getAllUserSessions(')) {
              try {
                const match = key.match(/getAllUserSessions\((.*?)\)/)
                if (match) {
                  const params = match[1] ? JSON.parse(match[1]) : undefined
                  const patch = dispatch(
                    userApi.util.updateQueryData('getAllUserSessions', params, (draft) => {
                      const session = draft.sessions.find((s) => s.id === sessionId)
                      if (session) {
                        session.isBookmarked = bookmarked
                      }
                    })
                  )
                  patches.push(patch)
                }
              } catch {
                // Skip if parsing fails
              }
            }
          })
        }

        try {
          await queryFulfilled
        } catch {
          patches.forEach((p) => p.undo())
        }
      },
      invalidatesTags: (_result, _error, { sessionId }) => [
        'Sessions',
        { type: 'Sessions', id: sessionId },
      ],
    }),
  }),
})

export const {
  useGetAllUserSessionsQuery,
  useGetSessionQuery,
  useDeleteSessionMutation,
  useUpdateSessionMutation,
  useSetSessionBookmarkMutation,
} = userApi
